import { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, IconButton, Link, Typography } from '@mui/material';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import PauseRoundedIcon from '@mui/icons-material/PauseRounded';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import { useTranslation } from 'react-i18next';
import { useAlert } from '@components/showAlert/useAlertHook';
import { apiErrorMessage } from '@functions/api-error-message';
import { AiService } from '@services/ai.service';
import { getCrmColors } from '../../CRM/kanban-board/crm-theme';
import { MultichannelMessage } from '../services/responses/multichannel.response';

interface Props {
    message: MultichannelMessage;
    /// O id de conversa se repete entre caixas: vai junto para a transcrição.
    inboxId?: number | null;
    cc: ReturnType<typeof getCrmColors>;
    /// Há modelo de áudio no servidor. Sem ele o áudio só toca.
    canTranscribe: boolean;
}

/// As mesmas do WhatsApp: quem ouve recado longo acelera.
const SPEEDS = [1, 1.5, 2];

const clock = (seconds: number) => {
    if (!Number.isFinite(seconds) || seconds < 0) return '0:00';

    const whole = Math.floor(seconds);
    return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
};

/// Áudio com a transcrição embaixo (gravada no servidor); player próprio, pois o nativo varia.
export const MessageAudio = ({ message, inboxId, cc, canTranscribe }: Props) => {
    const { t } = useTranslation();
    const { showAlert } = useAlert();

    const audioRef = useRef<HTMLAudioElement | null>(null);
    const [playing, setPlaying] = useState(false);
    const [current, setCurrent] = useState(0);
    const [duration, setDuration] = useState(0);
    const [speed, setSpeed] = useState(1);

    const [text, setText] = useState(message.transcription ?? null);
    const [loading, setLoading] = useState(false);

    // A lista recarrega a cada mensagem nova; o texto transcrito por outra pessoa chega junto.
    useEffect(() => {
        if (message.transcription) setText(message.transcription);
    }, [message.transcription]);

    // O timeupdate chega ~4x por segundo e a barra andava aos saltos: tocando, lê a cada quadro.
    useEffect(() => {
        if (!playing) return undefined;

        let frame = 0;
        const tick = () => {
            if (audioRef.current) setCurrent(audioRef.current.currentTime);
            frame = requestAnimationFrame(tick);
        };

        frame = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(frame);
    }, [playing]);

    const toggle = () => {
        const audio = audioRef.current;
        if (!audio) return;

        if (audio.paused) {
            audio.play().catch(() => showAlert(t('crmMultichannel.audioNaoToca'), 'error', 4000));
        } else {
            audio.pause();
        }
    };

    const nextSpeed = () => {
        const next = SPEEDS[(SPEEDS.indexOf(speed) + 1) % SPEEDS.length];
        setSpeed(next);
        if (audioRef.current) audioRef.current.playbackRate = next;
    };

    /// Clicar na barra pula para aquele ponto.
    const seek = (event: React.MouseEvent<HTMLDivElement>) => {
        const audio = audioRef.current;
        if (!audio || !duration) return;

        const rect = event.currentTarget.getBoundingClientRect();
        const ratio = Math.min(Math.max((event.clientX - rect.left) / rect.width, 0), 1);

        audio.currentTime = ratio * duration;
        setCurrent(audio.currentTime);
    };

    const transcribe = async () => {
        setLoading(true);

        try {
            const res = await new AiService().transcribe(message.conversationId, inboxId, message.id);
            if (res.data.data?.text) setText(res.data.data.text);
        } catch (err) {
            showAlert(apiErrorMessage(err), 'error', 5000);
        } finally {
            setLoading(false);
        }
    };

    // Na bolha enviada o fundo já é a cor de destaque: o botão fica claro.
    const buttonBg = message.outgoing ? 'rgba(255,255,255,.18)' : cc.accent;
    const trackBg = message.outgoing ? 'rgba(255,255,255,.25)' : cc.border;
    const fill = message.outgoing ? cc.textPrimary : cc.accent;
    const progress = duration ? Math.min(current / duration, 1) * 100 : 0;

    return (
        <Box sx={{ mb: message.content ? 0.7 : 0, width: 260, maxWidth: '100%' }}>
            <audio
                ref={audioRef}
                src={message.attachmentUrl!}
                preload="metadata"
                onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
                // Opus em WebM/OGG às vezes só revela a duração durante o play.
                onDurationChange={(e) => setDuration(e.currentTarget.duration)}
                // Parado, só muda por clique na barra ou no fim; tocando, pelo laço de quadros.
                onSeeked={(e) => setCurrent(e.currentTarget.currentTime)}
                onPlay={() => setPlaying(true)}
                onPause={() => setPlaying(false)}
                onEnded={() => { setPlaying(false); setCurrent(0); }}
            />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <IconButton
                    onClick={toggle}
                    aria-label={playing ? t('crmMultichannel.pausarAudio') : t('crmMultichannel.tocarAudio')}
                    sx={{
                        width: 34,
                        height: 34,
                        flexShrink: 0,
                        bgcolor: buttonBg,
                        color: '#fff',
                        '&:hover': { bgcolor: buttonBg, filter: 'brightness(1.12)' },
                    }}
                >
                    {playing
                        ? <PauseRoundedIcon sx={{ fontSize: 22 }} />
                        : <PlayArrowRoundedIcon sx={{ fontSize: 22 }} />}
                </IconButton>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box
                        role="slider"
                        aria-valuemin={0}
                        aria-valuemax={Math.round(duration)}
                        aria-valuenow={Math.round(current)}
                        onClick={seek}
                        sx={{ py: 0.9, cursor: duration ? 'pointer' : 'default' }}
                    >
                        <Box sx={{ position: 'relative', height: 4, borderRadius: 2, bgcolor: trackBg }}>
                            <Box
                                sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    width: `${progress}%`,
                                    borderRadius: 2,
                                    bgcolor: fill,
                                }}
                            />
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: `${progress}%`,
                                    width: 11,
                                    height: 11,
                                    borderRadius: '50%',
                                    bgcolor: fill,
                                    transform: 'translate(-50%, -50%)',
                                }}
                            />
                        </Box>
                    </Box>

                    <Typography
                        sx={{
                            fontSize: '0.7rem',
                            lineHeight: 1,
                            color: message.outgoing ? cc.textPrimary : cc.textMuted,
                            opacity: message.outgoing ? 0.8 : 1,
                            fontVariantNumeric: 'tabular-nums',
                        }}
                    >
                        {playing || current > 0 ? clock(current) : clock(duration)}
                    </Typography>
                </Box>

                <Box
                    component="button"
                    type="button"
                    onClick={nextSpeed}
                    sx={{
                        flexShrink: 0,
                        border: 'none',
                        cursor: 'pointer',
                        minWidth: 34,
                        px: 0.75,
                        py: 0.3,
                        borderRadius: '10px',
                        fontSize: '0.7rem',
                        fontWeight: 700,
                        color: message.outgoing ? cc.textPrimary : cc.textSecondary,
                        bgcolor: message.outgoing ? 'rgba(255,255,255,.15)' : cc.countBg,
                    }}
                >
                    {`${String(speed).replace('.', ',')}x`}
                </Box>
            </Box>

            {text ? (
                <Box
                    sx={{
                        mt: 0.8,
                        pl: 1,
                        borderLeft: `2px solid ${message.outgoing ? 'rgba(255,255,255,.35)' : cc.accent}`,
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: '0.68rem',
                            fontWeight: 700,
                            letterSpacing: '.04em',
                            textTransform: 'uppercase',
                            color: message.outgoing ? cc.textPrimary : cc.textMuted,
                            opacity: message.outgoing ? 0.75 : 1,
                        }}
                    >
                        {t('ia.transcricao')}
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{
                            fontSize: '0.84rem',
                            whiteSpace: 'pre-wrap',
                            overflowWrap: 'anywhere',
                            color: message.outgoing ? cc.textPrimary : cc.textSecondary,
                        }}
                    >
                        {text}
                    </Typography>
                </Box>
            ) : canTranscribe && (
                <Link
                    component="button"
                    underline="hover"
                    onClick={transcribe}
                    disabled={loading}
                    sx={{
                        mt: 0.6,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        fontSize: '0.78rem',
                        color: message.outgoing ? cc.textPrimary : cc.accent,
                    }}
                >
                    {loading
                        ? <CircularProgress size={11} color="inherit" />
                        : <GraphicEqIcon sx={{ fontSize: 14 }} />}
                    {loading ? t('ia.transcrevendo') : t('ia.transcrever')}
                </Link>
            )}
        </Box>
    );
};
